import React from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Plus } from 'lucide-react';
import { useVideoLessons } from './hooks/useVideoLessons';
import { VideoLessonDialog, VideoLessonsTable } from './components';
import { VideoLessonsPageProps } from './types';

export default function VideoLessonsPage({ isDark }: VideoLessonsPageProps) {
  const {
    videoLessons,
    isLoading,
    isCreateDialogOpen,
    isEditDialogOpen,
    formData,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
  } = useVideoLessons();

  const formatDate = (value: string) => {
    if (!value) return '-';
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={cn(
              'text-2xl lg:text-3xl font-bold tracking-tight',
              isDark ? 'text-white' : 'text-[#0F172A]'
            )}
          >
            Video Lessons
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Manage video lessons content
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
          <Plus className="w-4 h-4 mr-2" />
          Create Video Lesson
        </Button>
      </div>

      <Card
        className={cn(
          'rounded-2xl border-0 overflow-hidden transition-all duration-300',
          isDark ? 'bg-[#1E293B] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50'
        )}
      >
        <VideoLessonsTable
          isDark={isDark}
          videoLessons={videoLessons}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          formatDate={formatDate}
        />
      </Card>

      <VideoLessonDialog
        isDark={isDark}
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreate}
        title="Create Video Lesson"
        description="Add a new video lesson"
        submitLabel="Create"
      />

      <VideoLessonDialog
        isDark={isDark}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdate}
        title="Edit Video Lesson"
        description="Update video lesson information"
        submitLabel="Save Changes"
      />
    </div>
  );
}
